import { createStore } from "zustand";
import { Mutate, StoreApi } from "zustand/vanilla";

import { makeZustandContextStore } from "@/utils/zustandUtils.tsx";

export type PageDrawerItemType = "inclusionCenter" | "chat";

export type PageDrawerItemData = {
  isEnabled: boolean;
  isOpen: boolean;
};

export type PageDrawerData = Record<PageDrawerItemType, PageDrawerItemData>;

export type PageDrawerItemState = PageDrawerItemData & {
  touched: boolean;
  open: () => void;
  close: () => void;
  toggle: () => void;
  enable: () => void;
  disable: () => void;
};

export type PageDrawerState = Record<PageDrawerItemType, PageDrawerItemState>;

const initializeDrawerState = (
  item: PageDrawerItemType,
  data: PageDrawerItemData,
  setState: Mutate<StoreApi<PageDrawerState>, []>["setState"],
): PageDrawerItemState => ({
  isEnabled: data.isEnabled,
  isOpen: data.isOpen,
  touched: false,
  open: () =>
    setState(state => ({
      [item]: {
        ...state[item],
        isOpen: true,
        touched: true,
      },
    })),
  close: () =>
    setState(state => ({
      [item]: {
        ...state[item],
        isOpen: false,
        touched: true,
      },
    })),
  toggle: () =>
    setState(state => ({
      [item]: {
        ...state[item],
        isOpen: !state[item].isOpen,
        touched: true,
      },
    })),
  enable: () =>
    setState(state => ({
      [item]: {
        ...state[item],
        isEnabled: true,
      },
    })),
  disable: () =>
    setState(state => ({
      [item]: {
        ...state[item],
        isEnabled: false,
      },
    })),
});

const createPageWithDrawerStore = (initProps: PageDrawerData) => {
  return createStore<PageDrawerState>()(set => ({
    chat: initializeDrawerState("chat", initProps.chat, set),
    inclusionCenter: initializeDrawerState("inclusionCenter", initProps.inclusionCenter, set),
  }));
};

const [, _PageWithDrawerProvider, _useDrawerContext] = makeZustandContextStore<PageDrawerData, PageDrawerState>(
  "PageDrawerStore",
  createPageWithDrawerStore,
);

export const PageDrawerProvider = _PageWithDrawerProvider;
const chatSelector = (state: PageDrawerState) => state.chat;
export const useChatDrawer = () => _useDrawerContext(chatSelector);
const inclusionCenterSelector = (state: PageDrawerState) => state.inclusionCenter;
export const useInclusionCenterDrawer = () => _useDrawerContext(inclusionCenterSelector);
